import React, { useEffect, useState } from "react";
import { fetchInvoices } from "../../services/api/billing/invoiceService";
import {
    getPaymentsByInvoice,
    createPayment,
    uploadPaymentAttachment,
    downloadPayment
} from "../../services/api/billing/paymentService";

import toast from "react-hot-toast";
import { Plus, X, Download } from "lucide-react";

const InvoicePaymentPage = () => {

    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        amount: "",
        payment_date: "",
        mode: "upi",
        txn_ref_no: "",
        attachment: null,
    });

    // ✅ Load invoices with pagination
    const loadInvoices = async () => {
        try {
            const res = await fetchInvoices({ search, page });
            setInvoices(res.data || []);
        } catch {
            toast.error("Failed to load invoices");
        }
    };

    useEffect(() => {
        loadInvoices();
    }, [search, page]);

    // ✅ Load payments when invoice selected
    const loadPayments = async (invoiceId) => {
        setLoading(true);
        try {
            const res = await getPaymentsByInvoice(invoiceId);
            setPayments(res?.data?.data?.data || []);
        } catch {
            toast.error("Failed to load payments");
        }
        setLoading(false);
    };

    const handleSelectInvoice = (inv) => {
        setSelectedInvoice(inv);
        loadPayments(inv.id);
    };

    // ✅ Create Payment
    const handleSave = async () => {
        try {
            const res = await createPayment({
                invoice_id: selectedInvoice.id,
                amount: Number(form.amount),
                payment_date: form.payment_date,
                mode: form.mode,
                txn_ref_no: form.txn_ref_no
            });

            const paymentId = res?.data?.id ?? res?.id;

            if (form.attachment) {
                const fd = new FormData();
                fd.append("attachment", form.attachment);
                await uploadPaymentAttachment(paymentId, fd);
            }

            toast.success("Payment added");
            setShowForm(false);
            loadPayments(selectedInvoice.id);

        } catch {
            toast.error("Failed to save");
        }
    };

    return (
        <div className="p-4">

            {/* 🔍 Invoice Search */}
            <input
                type="text"
                placeholder="Search invoices..."
                className="border px-3 py-2 mb-3 w-full"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />

            {/* 📋 Invoice Table */}
            <table className="w-full text-sm bg-white shadow rounded mb-6">
                <thead className="bg-gray-100">
                    <tr>
                        <th className="p-2">ID</th>
                        <th className="p-2">Total</th>
                        <th className="p-2">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {invoices.map(inv => (
                        <tr
                            key={inv.id}
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleSelectInvoice(inv)}
                        >
                            <td className="p-2">{inv.id}</td>
                            <td className="p-2">₹{inv.total}</td>
                            <td className="p-2">{inv.status}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* 💳 Payments Section */}
            {selectedInvoice && (
                <>
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold">
                            Payments (Invoice #{selectedInvoice.id})
                        </h2>

                        <button
                            onClick={() => setShowForm(!showForm)}
                            className="bg-blue-600 text-white px-3 py-2 rounded flex gap-2"
                        >
                            {showForm ? <X size={16} /> : <Plus size={16} />}
                            Add Payment
                        </button>
                    </div>

                    {/* ➕ Form */}
                    {showForm && (
                        <div className="bg-white p-4 rounded shadow mb-4 grid md:grid-cols-4 gap-3">

                            <input type="number" placeholder="Amount"
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                className="border p-2"
                            />

                            <input type="date"
                                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                                className="border p-2"
                            />

                            <select
                                onChange={(e) => setForm({ ...form, mode: e.target.value })}
                                className="border p-2"
                            >
                                <option value="upi">UPI</option>
                                <option value="cash">Cash</option>
                                <option value="bank">Bank</option>
                            </select>

                            <input type="text" placeholder="Txn Ref No"
                                onChange={(e) => setForm({ ...form, txn_ref_no: e.target.value })}
                                className="border p-2"
                            />

                            <input type="file"
                                onChange={(e) => setForm({ ...form, attachment: e.target.files[0] })}
                            />

                            <button onClick={handleSave} className="bg-green-600 text-white p-2">
                                Save
                            </button>
                        </div>
                    )}

                    {/* 📊 Payments */}
                    <table className="w-full bg-white shadow rounded">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Amount</th>
                                <th>Date</th>
                                <th>Mode</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map(p => (
                                <tr key={p.id}>
                                    <td>{p.id}</td>
                                    <td>₹{p.amount}</td>
                                    <td>{p.payment_date}</td>
                                    <td>{p.mode}</td>
                                    <td>
                                        <button onClick={() => downloadPayment(p.id)}>
                                            <Download size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

        </div>
    );
};

export default InvoicePaymentPage;